import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Settings2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { section: "one_way_ai", key: "transcriptions", label: "Transcriptions", desc: "Alfred transcribes video answers." },
  { section: "one_way_ai", key: "ai_summary", label: "Candidate Summary", desc: "Show a short Candidate Summary above each answer." },
  { section: "one_way_ai", key: "ai_video_review", label: "AI Video Review", desc: "Rank candidates by AI Video Review." },
  { section: "proctoring", key: "flag_ai_generated", label: "Flag AI-generated content", desc: "Beta — highlight likely AI-generated answers." },
  { section: "candidate_videos", key: "hide_from_reviewers", label: "Hide candidate videos from reviewers", desc: "Audio-only review to reduce bias." },
];

export default function JobSettingsOverride({ jobId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open || !jobId) return;
    api.get(`/recruit/jobs/${jobId}/settings`).then(r => { setData(r.data); setDirty(false); }).catch(() => {});
  }, [open, jobId]);

  if (!data) return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="job-override-dialog">
        <DialogHeader><DialogTitle>Job settings</DialogTitle></DialogHeader>
        <div className="py-8 text-center text-neutral-500 text-sm">Loading…</div>
      </DialogContent>
    </Dialog>
  );

  const effVal = (section, key) => data.effective?.[section]?.[key];
  const orgVal = (section, key) => data.org_defaults?.[section]?.[key];
  const overridden = (section, key) => !!data.overrides?.[section] && data.overrides[section][key] !== undefined;

  const toggle = (section, key) => {
    const cur = effVal(section, key);
    const next = !cur;
    const overridesCopy = JSON.parse(JSON.stringify(data.overrides || {}));
    // If flipping to match org default, remove override; else set it
    if (next === orgVal(section, key)) {
      if (overridesCopy[section]) {
        delete overridesCopy[section][key];
        if (Object.keys(overridesCopy[section]).length === 0) delete overridesCopy[section];
      }
    } else {
      overridesCopy[section] = { ...(overridesCopy[section] || {}), [key]: next };
    }
    const nextEffective = { ...data.effective };
    nextEffective[section] = { ...data.org_defaults[section], ...(overridesCopy[section] || {}) };
    setData({ ...data, overrides: overridesCopy, effective: nextEffective });
    setDirty(true);
  };

  const resetAll = () => {
    setData({ ...data, overrides: {}, effective: { ...data.org_defaults } });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        one_way_ai: data.overrides.one_way_ai || {},
        proctoring: data.overrides.proctoring || {},
        candidate_videos: data.overrides.candidate_videos || {},
      };
      const { data: fresh } = await api.put(`/recruit/jobs/${jobId}/settings`, payload);
      setData(fresh);
      setDirty(false);
      toast.success("Job settings updated");
      onOpenChange(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const anyOverride = Object.keys(data.overrides || {}).length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="job-override-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-pink" /> Job settings override
          </DialogTitle>
          <DialogDescription className="text-xs text-neutral-500">
            Override org defaults just for this job. Untouched toggles stay in sync with the org-wide Job settings page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {FIELDS.map(f => {
            const eff = !!effVal(f.section, f.key);
            const isOverride = overridden(f.section, f.key);
            return (
              <div key={`${f.section}.${f.key}`} className="flex items-start justify-between gap-4 py-2 border-b last:border-b-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium">{f.label}</div>
                    {isOverride
                      ? <Badge className="text-[9px] bg-pink text-white border-0" data-testid={`override-badge-${f.key}`}>Override</Badge>
                      : <Badge variant="outline" className="text-[9px] text-neutral-500">Inherited</Badge>}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">{f.desc}</div>
                </div>
                <Switch checked={eff} onCheckedChange={() => toggle(f.section, f.key)} data-testid={`override-toggle-${f.key}`} />
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={resetAll} disabled={!anyOverride} data-testid="reset-overrides-btn" className="text-neutral-500">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset all to org default
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} disabled={saving || !dirty} data-testid="save-override-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
